/**
 * Structured provider names shared between the main app (`src/`) and the voice-agent runtime
 * (`livekit/`). A scheduling provider's `displayName` (e.g. "Renzo Cataldo, MD") is a written label;
 * `ProviderName` holds the parts so spoken forms and STT hints are derived rather than re-parsed.
 */
import { z } from "zod";

const providerNameShape = {
  /** The given name the provider goes by (e.g. "Keith" for "L. Keith Routh"), not an initial. */
  givenName: z.string().trim().min(1),
  familyName: z.string().trim().min(1),
  /** Other names callers may use, such as a former family name. */
  otherNames: z.array(z.string().trim().min(1)).optional(),
  /** Professional credentials in display order (e.g. ["MD"], ["PA-C"]). Never spoken as part of the name. */
  credentials: z.array(z.string().trim().min(1)).optional(),
  /** Set only when the practice addresses this provider with the title (e.g. "Dr. Cataldo"). */
  honorific: z.literal("Dr.").optional(),
};

/** Strict for hand-authored schedule.yaml, so typos in field names fail validation. */
export const providerNameSchema = z.object(providerNameShape).strict();

export type ProviderName = z.infer<typeof providerNameSchema>;

/**
 * A scheduling provider as the voice agent sees it; `name` is null until the roster is backfilled.
 * Lenient because agents and the web app deploy separately: unknown keys are stripped, and a name an
 * older agent can't parse (e.g. a new honorific) degrades to displayName parsing instead of failing
 * the whole lookup.
 */
export const voiceProviderIdentitySchema = z.object({
  displayName: z.string(),
  name: z.object(providerNameShape).nullable().catch(null),
});

export type VoiceProviderIdentity = z.infer<typeof voiceProviderIdentitySchema>;

function splitHyphenatedName(name: string): string[] {
  const parts = name
    .split(/[-\u2010\u2011]/u)
    .map((part) => part.trim())
    .filter(Boolean);

  return parts.length > 1 ? parts : [name];
}

export function getProviderSttNameVariants(names: readonly string[]): string[] {
  return names.flatMap(splitHyphenatedName);
}

/** Separate STT hints for each name, since callers may ask for a provider by any one of them. */
export function getProviderSttNames(name: ProviderName): string[] {
  const canonicalNames = [name.givenName, name.familyName, ...(name.otherNames ?? [])];

  // Spoken keyterms represent the individual name components; punctuation inside a compound name
  // is written roster data and is not pronounced by the caller.
  return getProviderSttNameVariants(canonicalNames);
}

// Compared after removing periods and hyphens, so "M.D." and "PA-C" match "MD" and "PAC".
const PROVIDER_CREDENTIALS = new Set([
  "ACNP",
  "AGACNP",
  "AGACNPBC",
  "AGPCNPBC",
  "AGPCNPC",
  "AGNP",
  "AGNPC",
  "ANP",
  "APRN",
  "ARNP",
  "AUD",
  "CCCA",
  "CNM",
  "CNP",
  "CRNA",
  "CRNP",
  "DDS",
  "DMD",
  "DNP",
  "DO",
  "DPM",
  "DPT",
  "FNP",
  "FNPBC",
  "FNPC",
  "LCSW",
  "MBBS",
  "MD",
  "MS",
  "MSN",
  "NP",
  "NPC",
  "OD",
  "PA",
  "PAC",
  "PHD",
  "PMHNP",
  "PMHNPBC",
  "PSYD",
  "RN",
]);

// Some rosters spell the role out instead of abbreviating it (e.g. "Karan Saini, Physician Assistant").
const SPELLED_OUT_CREDENTIALS = new Set(["nurse practitioner", "physician assistant"]);

// Credentials are written in capitals or with periods; title-case tokens are more likely names
// ("Smith, Do" in a "Last, First" roster). These doctorates are conventionally mixed case.
const MIXED_CASE_CREDENTIALS = new Set(["AuD", "PhD", "PsyD"]);

/** Canonical comparison key: "M.D." and "MD" → "MD"; "PA-C" → "PAC". */
export function toCredentialKey(credential: string): string {
  return credential.replace(/[.-]/g, "").toUpperCase();
}

/** Case-insensitive check for normalized comparison keys, where casing carries no signal. */
export function isProviderCredentialKey(token: string): boolean {
  return PROVIDER_CREDENTIALS.has(toCredentialKey(token));
}

export function isProviderCredential(token: string): boolean {
  const isWrittenAsCredential =
    token === token.toUpperCase() || token.includes(".") || MIXED_CASE_CREDENTIALS.has(token);

  return isWrittenAsCredential && isProviderCredentialKey(token);
}

// A credential segment may carry an annotation, e.g. "DPT (Physical Therapy — out of scope)".
function parseCredentialSegment(segment: string): string[] | null {
  const withoutAnnotation = segment.replace(/\s*\([^)]*\)\s*$/, "").trim();

  if (SPELLED_OUT_CREDENTIALS.has(withoutAnnotation.toLowerCase())) return [withoutAnnotation];

  const tokens = withoutAnnotation.split(/\s+/);

  return withoutAnnotation && tokens.every(isProviderCredential) ? tokens : null;
}

export interface ParsedProviderDisplayName {
  /** The display name without recognized trailing credentials or a leading "Dr." */
  spokenName: string;
  credentials: string[];
  hasDoctorTitle: boolean;
}

/**
 * Best-effort split of a free-text display label. Only recognized trailing credentials are removed,
 * because a comma can also separate family and given names. Prefer a configured `ProviderName`.
 */
export function parseProviderDisplayName(displayName: string): ParsedProviderDisplayName {
  const parts = displayName
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  const credentials: string[] = [];

  while (parts.length > 1) {
    const segmentCredentials = parseCredentialSegment(parts[parts.length - 1]);

    if (!segmentCredentials) break;

    credentials.unshift(...segmentCredentials);
    parts.pop();
  }

  const joined = parts.join(" ");
  const withoutTitle = joined.replace(/^Dr\.?\s+/i, "");

  return { spokenName: withoutTitle, credentials, hasDoctorTitle: withoutTitle !== joined };
}
